import { Paper, Typography, Grid, Box, ClickAwayListener, Divider } from '@material-ui/core';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { StyledButton } from '../../../common/components/Button';
import LanguageSelectionForm from '../../../common/components/LanguageSelectionForm';

import { GeneralContentProps, MediaSource } from '../../interfaces/generalContentProps';
import { GeneralContentStyles } from '../../styles';
import BackButton from '../BackButton';
import clsx from 'clsx';
import CkEditorContent from '../../../common/components/CkEditorContent';
import { hexToRgba } from '../../utils/generalContent';
import { appendCdnToImagePath } from '../../../common/utils/appendCdnToImagePath';
import { BrandColors, WorkflowItemCode } from '../../../constants';
import { genericJourneyTheme } from '../../../common/styles/genericJourneyTheme';
import ReactPlayer from 'react-player';
import { clearUserSessionTimers, useActivateUserSessionTimers } from '../../../common/utils/userSessionTimers';
import { sendGeneralContentVideoDetails } from '../../../common/utils/gaEvents';
import { Skeleton } from '@material-ui/lab';
import VideoTaskLayout from '../../../videoTask/components/layout_01/VideoTaskLayout';
import { videoTaskDefaultConfiguration } from '../../../videoTask/constants/defaultConfiguration';
import localizeContentMapper from '../../../common/utils/localizeContentMapper';
import Transition from '../../../verticalTransition/components/Transition';
import { verticalTimeline } from '../../../verticalTransition/styles/verticalTimeline';
import { CDNImage } from '../../../common/interfaces/cdnImage';
import { TimeLineStyle } from '../../../store/interfaces';
import { commonStyles } from '../../../common/styles/commonStyle';
import { useAppSelector } from '../../../redux-store/hook';

const GeneralContentLayout5: React.FC<GeneralContentProps> = (props: GeneralContentProps): JSX.Element => {
  const {
    backgroundImageEnabled,
    backgroundColor,
    backgroundImage,
    contentContainerBackgroundColor,
    contentContainerOpacity,
    buttonText,
    buttonStyle,
    content,
    isICPEnabled,
    schedulableTaskItem,
    mediaSource,
    mediaUrl,
    currentWorkflowItem,
    isNextItemAssessment,
    timeline: timelineContentPage,
  } = props;
  const candidate = useAppSelector((state) => state.candidate);
  const {
    settings: {
      page: { backgroundImage: bgImage, timeline, containerColor },
    },
  } = useAppSelector((state) => state.template);

  const [popup, setPopup] = useState(false);
  const VideoPlayer = React.lazy(() => import('../../../common/components/VideoPlayer'));
  const template = useAppSelector((state) => state.template);
  const contentBgColor = hexToRgba(
    contentContainerBackgroundColor ? contentContainerBackgroundColor : containerColor || BrandColors.white,
    contentContainerOpacity,
  );
  const commonTheme = commonStyles({
    contentContainerColor: contentBgColor,
    backButtonEnabled: props.returnToTaskList,
  });
  const [showShadow, setShowShadow] = useState<boolean>(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  let url = '';
  const imageUrl = mediaUrl as CDNImage;
  if (imageUrl) {
    url = imageUrl.path;
  }

  const genericPath =
    backgroundImageEnabled && backgroundImage
      ? backgroundImage.path
      : backgroundImageEnabled && !backgroundImage
      ? bgImage?.path
      : '';
  const backgroundImagePath = genericPath ? appendCdnToImagePath(genericPath) : '';

  const classes = GeneralContentStyles({
    backgroundColor,
    backgroundImage: backgroundImagePath,
    contentBgColor,
    isICPEnabledCorrin: isICPEnabled,
    url,
  });

  const classesJourneyTheme = genericJourneyTheme();
  const { userSessionActive } = useAppSelector((state) => state.basicDetails);
  const activateUserSessionTimers = useActivateUserSessionTimers();

  const onVideoPlay = (currentRef: React.RefObject<ReactPlayer>): void => {
    console.log('VideoPlayed:', currentRef.current?.getCurrentTime());
    if (userSessionActive) {
      clearUserSessionTimers();
      activateUserSessionTimers();
    }
  };

  const onVideoPause = (currentRef: React.RefObject<ReactPlayer>): void => {
    console.log('VideoPaused:', currentRef.current?.getCurrentTime(), currentRef.current);
    /** GA Event on Video pause */
    // sendGeneralContentVideoDetails(currentDuration, totalDuration, pageStartTime, url, '');
  };

  /**
   * This change is specific to Corrin layout and solves issue CAW-1078.
   * If html overflow is hidden, then mui select will cause the issue in IE.
   * For IE, we need overflow to be hidden to solve background jitter.
   * Here, we set to hidden on mount, and reset on unmount.
   */
  React.useLayoutEffect(() => {
    if (window.navigator.userAgent.includes('Trident')) {
      document.documentElement.style.overflow = props.isLanguageSelectionFormOpen ? '' : 'hidden';

      return (): void => {
        document.documentElement.style.overflow = '';
      };
    }
  });

  useEffect(() => {
    if (scrollRef.current) {
      const cur = scrollRef.current;
      // used to show bottom shadow when scroller comes in desktop view
      if (cur.clientHeight < cur.scrollHeight) {
        setShowShadow(true);
      } else setShowShadow(false);
    }
  }, []);

  const currentTimelineStyle: TimeLineStyle = {
    lineColor: timelineContentPage?.lineColor || timeline.lineColor,
    iconContainerColor: timelineContentPage?.iconContainerColor || timeline.iconContainerColor,
    iconColor: timelineContentPage?.iconColor || timeline.iconColor,
  };

  const classesTransition = verticalTimeline(currentTimelineStyle);
  const getTotalVideoDuration = (currentRef: React.RefObject<ReactPlayer>): void => {
    console.log('TotalVideoDuration', currentRef.current?.getDuration());
  };

  const getVideoEnd = (currentRef: React.RefObject<ReactPlayer>, url: string): void => {
    console.log('Video Ended:', currentRef.current?.getCurrentTime());
    const currentDuration = currentRef.current?.getCurrentTime();
    const totalDuration = currentRef.current?.getDuration();
    /** GA Event : on video end */
    sendGeneralContentVideoDetails(currentDuration, totalDuration, new Date(), url, '', '', { template, candidate });
  };

  const handleClick = (): void => {
    setPopup(true);
  };

  const parsedConfig =
    currentWorkflowItem && currentWorkflowItem.configuration && currentWorkflowItem.code === WorkflowItemCode.GENCONTENT
      ? JSON.parse(currentWorkflowItem.configuration).configWidgetJSON
      : videoTaskDefaultConfiguration;

  const videoTaskConfiguration = currentWorkflowItem
    ? localizeContentMapper(parsedConfig, currentWorkflowItem.localizedContentMap)
    : parsedConfig;

  videoTaskConfiguration['videoUrl'] = mediaUrl;
  videoTaskConfiguration['buttonText'] = null;

  const VideoContainer = ({ url }: { url: string }): JSX.Element => {
    return (
      <div className={classes.mediaContainer} onClick={handleClick}>
        <Suspense fallback={<Skeleton height="100vh" />}>
          <VideoPlayer
            isDefaultPlayIcon={true}
            url={url}
            lightMode={true}
            onVideoPlay={onVideoPlay}
            onVideoPause={onVideoPause}
            getTotalVideoDuration={getTotalVideoDuration}
            getVideoEnd={getVideoEnd}
          />
        </Suspense>
      </div>
    );
  };

  const mediaContent = (url: string): JSX.Element => {
    return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url) ? (
      <div className={classes.mediaContainer}>
        <img src={appendCdnToImagePath(url)} className={`${classesJourneyTheme.ImgContainer}`} alt="shlimage" />
      </div>
    ) : (
      <VideoContainer url={url} />
    );
  };

  return (
    <>
      <Grid
        data-testid="background-Corrin"
        className={`${clsx({
          [classes.bgImageCorrin]: backgroundImageEnabled,
          [classes.bgColorContainer]: true,
          [classes.minGridTransitionHeight]: true,
        })} `}>
        <div className={classesTransition.verticalGridContentPage}></div>
        <div className={classesJourneyTheme.upLayer}>
          <Transition isTrans={true} isNextItemAssessment={isNextItemAssessment} timelineStyles={currentTimelineStyle}>
            <Grid container alignItems="center" justify="center" className={classesJourneyTheme.journeyThemeGrid}>
              <Grid item xs={12}>
                <Paper
                  elevation={contentContainerOpacity === 0 ? 0 : 1}
                  className={`
              ${classes.bgColorCard} 
              ${classesJourneyTheme.journeyThemePaper} 
              ${showShadow ? commonTheme.bottomShadow : ''}
              `}>
                  <Box>
                    {backgroundImageEnabled && (
                      <Typography variant="srOnly">{`Background Image ${
                        backgroundImage && backgroundImage.name
                      }`}</Typography>
                    )}
                    {popup ? (
                      <VideoTaskLayout
                        videoTaskConfiguration={videoTaskConfiguration}
                        closePopup={(): void => setPopup(false)}
                        isVideoFromJourney={true}
                      />
                    ) : null}

                    <Typography variant="srOnly">{`Background Color ${backgroundColor}`}</Typography>
                  </Box>

                  {/** Content Container */}
                  <div ref={scrollRef} className={`${classesJourneyTheme.journeyThemeScroll}`}>
                    {mediaSource === MediaSource.URL && <VideoContainer url={mediaUrl as string} />}
                    {mediaSource === MediaSource.MEDIA_LIBRARY && mediaContent((mediaUrl as CDNImage).path)}

                    <Divider component="div" className={classesJourneyTheme.customDivider} />

                    <CkEditorContent
                      content={content}
                      pageStartTime={props.pageStartTime}
                      generalContentName={props.name}
                      taskName={props.taskName}
                    />

                    {/** Button */}

                    <Box
                      width="100%"
                      textAlign="center"
                      className={`
                        ${classes.gridButton} 
                        ${classesJourneyTheme.upLayer}`}>
                      <StyledButton
                        buttonText={buttonText}
                        buttonStyle={buttonStyle}
                        disabled={props.loading || props.isNextButtonDisabled}
                        onClickHandler={props.saveNGetNextWfItem}
                      />

                      {props.returnToTaskList && <BackButton />}
                    </Box>
                  </div>

                  {props.isLanguageSelectionFormOpen && !schedulableTaskItem && (
                    <Box className={`${classesJourneyTheme.upLayer}`}>
                      <div className={`${classesJourneyTheme.languageOverlay}`}></div>
                      <ClickAwayListener onClickAway={props.handleClickAwayLanguageSelection}>
                        <Box className={classes.languageSelectionOverlay}>
                          <Box textAlign="left" className={classes.languageSelectionModal}>
                            <LanguageSelectionForm
                              languages={props.languages}
                              onLanguageSubmit={props.onLanguageSubmit}
                            />
                          </Box>
                        </Box>
                      </ClickAwayListener>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Transition>
        </div>
      </Grid>
    </>
  );
};

export default GeneralContentLayout5;
